"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { seedData } from "@/data/seed";
import { callPortal, cloudConfig, getCloudClient, loadOwnerSnapshot, mapSnapshot, readPortalAccess, type CloudStatus } from "@/lib/cloud";
import { recordDiagnostic } from "@/lib/aqua/diagnostics";
import { buildHoldbackLedger, deriveDrawInvoices, drawApprovedAmount, drawCommittedAmount, effectiveDrawInvoices, gcFeeForSubtotal, holdbackForAmount, invoicePaymentDrawIds, normalizeGcFeePercent } from "@/lib/financial";
import { AQUA_PUBLIC_APP_URL, buildClientPortalUrl } from "@/lib/publicConfig";
import { buildHomeownerDecisionBatch, type HomeownerDecisionDraft, type HomeownerDecisionDrafts } from "@/lib/homeownerReview";
import type { AppData, Client, CompanyProfile, Draw } from "@/lib/types";

const STORAGE_KEY = "aquahomes-os-draw-alpha-v2";
const EMPTY_DATA: AppData = { companyProfile: null, invoices: [], clients: [], jobs: [], sowItems: [], draws: [] };

function loadLocalData(): AppData {
  if (typeof window === "undefined") return seedData;
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (!raw) return seedData;
  try {
    const parsed = JSON.parse(raw) as Partial<AppData>;
    return { ...seedData, ...parsed, invoices: parsed.invoices || [] };
  } catch { return seedData; }
}

async function sha256Hex(value: string) {
  const bytes = new TextEncoder().encode(value);
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
}

function createPortalToken() {
  const bytes = crypto.getRandomValues(new Uint8Array(32));
  return btoa(String.fromCharCode(...bytes)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function dataUrlToBlob(value: string) {
  const [header, encoded] = value.split(",", 2);
  const type = header.match(/^data:([^;]+)/)?.[1] || "image/jpeg";
  const binary = atob(encoded || "");
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return new Blob([bytes], { type });
}

function createRequestNumber(requestId: string, createdAt: string) {
  return `DRW-${createdAt.slice(0, 10).replaceAll("-", "")}-${requestId.replaceAll("-", "").slice(0, 8).toUpperCase()}`;
}

function validatePhotoFile(file: File) {
  if (!file.type.startsWith("image/")) throw new Error(`${file.name} is not a supported image.`);
  if (file.size > 12 * 1024 * 1024) throw new Error(`${file.name} is too large. Choose a photo under 12 MB.`);
}

function validateLogoFile(file: File) {
  const allowed = new Set(["image/png", "image/jpeg", "image/webp"]);
  if (!allowed.has(file.type)) throw new Error("Choose a PNG, JPEG, or WebP company logo.");
  if (file.size > 5 * 1024 * 1024) throw new Error("Choose a company logo under 5 MB.");
}

function fileToDataUrl(file: File) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("The selected image could not be read."));
    reader.onload = () => resolve(String(reader.result));
    reader.readAsDataURL(file);
  });
}

export function useAppStore() {
  const [data, setData] = useState<AppData>(EMPTY_DATA);
  const [hydrated, setHydrated] = useState(false);
  const [cloudStatus, setCloudStatus] = useState<CloudStatus>("connecting");
  const [cloudError, setCloudError] = useState("");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [syncing, setSyncing] = useState(false);
  const portalClientIdRef = useRef("");
  const portalTokenRef = useRef("");
  const mountedRef = useRef(true);
  const refreshPromiseRef = useRef<Promise<void> | null>(null);

  const refresh = useCallback(async () => {
    if (refreshPromiseRef.current) return refreshPromiseRef.current;
    const nextRefresh = (async () => {
      const portalClientId = portalClientIdRef.current;
      if (portalClientId) {
        const portalToken = portalTokenRef.current;
        if (!portalToken) {
          if (mountedRef.current) { setData(EMPTY_DATA); setCloudStatus("portal-signed-out"); }
          return;
        }
        const payload = await callPortal(portalClientId, portalToken);
        if (mountedRef.current) {
          setData(mapSnapshot(payload));
          setCloudStatus("portal");
          setCloudError("");
        }
        return;
      }
      const supabase = getCloudClient();
      if (!supabase) return;
      const { data: auth } = await supabase.auth.getSession();
      if (!auth.session) {
        if (mountedRef.current) { setData(EMPTY_DATA); setCloudStatus("signed-out"); }
        return;
      }
      const snapshot = await loadOwnerSnapshot(supabase);
      if (mountedRef.current) {
        setData(snapshot);
        setOwnerEmail(auth.session.user.email || "");
        setCloudStatus("ready");
        setCloudError("");
      }
    })();
    refreshPromiseRef.current = nextRefresh;
    try {
      await nextRefresh;
    } finally {
      if (refreshPromiseRef.current === nextRefresh) refreshPromiseRef.current = null;
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    const config = cloudConfig();
    const portalAccess = readPortalAccess();
    portalClientIdRef.current = portalAccess.clientId;
    portalTokenRef.current = portalAccess.portalToken;
    const supabase = getCloudClient();
    if (!config.configured || !supabase) {
      const localData = loadLocalData();
      queueMicrotask(() => {
        if (!mountedRef.current) return;
        setData(localData);
        setCloudStatus("offline-demo");
        setHydrated(true);
      });
      return () => { mountedRef.current = false; };
    }
    void refresh().catch((error: unknown) => {
      if (!mountedRef.current) return;
      setCloudStatus("error");
      setCloudError(error instanceof Error ? error.message : "Aqua Draw could not load the shared workspace.");
    }).finally(() => { if (mountedRef.current) setHydrated(true); });
    const { data: listener } = supabase.auth.onAuthStateChange((event) => {
      if (!["SIGNED_IN", "SIGNED_OUT", "USER_UPDATED"].includes(event)) return;
      void refresh().catch(() => undefined);
    });
    return () => {
      mountedRef.current = false;
      listener.subscription.unsubscribe();
    };
  }, [refresh]);

  useEffect(() => {
    if (hydrated && cloudStatus === "offline-demo") window.localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  }, [cloudStatus, data, hydrated]);

  const runRemote = useCallback((event: string, task: () => Promise<unknown>) => {
    if (cloudStatus === "offline-demo") return;
    setSyncing(true);
    setCloudError("");
    void task().then(() => refresh()).then(() => {
      recordDiagnostic({ level: "PASS", category: "network", event, message: "Shared workspace change saved and verified." });
    }).catch((error: unknown) => {
      const message = error instanceof Error ? error.message : "The shared workspace rejected the change.";
      setCloudError(message);
      recordDiagnostic({ level: "FAIL", category: "network", event, message });
      void refresh().catch(() => undefined);
    }).finally(() => setSyncing(false));
  }, [cloudStatus, refresh]);

  const actions = useMemo(() => {
    const approveInvoice = async (requestId: string, holdbackPercent: number, holdbackReason: string) => {
      const percent = Number(holdbackPercent);
      const reason = holdbackReason.trim();
      if (!Number.isInteger(percent) || percent < 0 || percent > 15) throw new Error("Holdback must be a whole percentage between 0 and 15.");
      if (percent > 0 && !reason) throw new Error("Explain why money is being held back.");
      if (reason.length > 1000) throw new Error("Holdback explanation must be 1,000 characters or fewer.");
      const requestDraws = data.draws.filter((draw) => (draw.requestId || draw.id) === requestId);
      if (!requestDraws.length) throw new Error("Invoice not found.");
      if (requestDraws.some((draw) => draw.status !== "CLIENT_REVIEW")) throw new Error("This invoice is no longer awaiting approval.");
      const approvedAt = new Date();
      const arrival = new Date(approvedAt.getTime() + 48 * 60 * 60 * 1000).toISOString();
      const applyApproval = (current: AppData) => {
        const draws = current.draws.map((draw) => {
          if ((draw.requestId || draw.id) !== requestId || draw.status !== "CLIENT_REVIEW") return draw;
          const amounts = holdbackForAmount(draw.amount, percent);
          return {
            ...draw,
            status: "APPROVED" as const,
            approvedAmount: amounts.approvedAmount,
            withheldAmount: amounts.withheldAmount,
            holdbackPercent: percent,
            holdbackReason: percent > 0 ? reason : "",
            estimatedArrivalDate: arrival,
          };
        });
        const next = { ...current, draws, invoices: [] };
        return { ...next, invoices: deriveDrawInvoices(next) };
      };

      if (cloudStatus === "offline-demo") {
        setData(applyApproval);
        return;
      }
      if (!portalClientIdRef.current) throw new Error("Only the client portal can approve an invoice.");
      setSyncing(true);
      setCloudError("");
      try {
        await callPortal(portalClientIdRef.current, portalTokenRef.current, {
          action: "approve-invoice",
          requestId,
          holdbackPercent: percent,
          holdbackReason: percent > 0 ? reason : "",
        });
        await refresh();
        recordDiagnostic({ level: "PASS", category: "network", event: "data.invoice-approved", message: "Invoice approval and holdback were saved and verified." });
      } catch (error) {
        const message = error instanceof Error ? error.message : "The shared workspace rejected this invoice approval.";
        setCloudError(message);
        recordDiagnostic({ level: "FAIL", category: "network", event: "data.invoice-approved", message });
        await refresh().catch(() => undefined);
        throw error;
      } finally {
        setSyncing(false);
      }
    };

    const denyInvoice = async (requestId: string, denialReason: string) => {
      const reason = denialReason.trim();
      if (!reason) throw new Error("Enter a reason before denying this invoice.");
      if (reason.length > 1000) throw new Error("The denial reason must be 1,000 characters or fewer.");
      const requestDraws = data.draws.filter((draw) => (draw.requestId || draw.id) === requestId);
      if (!requestDraws.length) throw new Error("Invoice not found.");
      if (requestDraws.some((draw) => draw.status !== "CLIENT_REVIEW")) throw new Error("This invoice is no longer awaiting a homeowner decision.");

      const applyDenial = (current: AppData) => {
        const draws = current.draws.map((draw) => (draw.requestId || draw.id) === requestId && draw.status === "CLIENT_REVIEW"
          ? {
              ...draw,
              status: "CHANGES_REQUESTED" as const,
              approvedAmount: undefined,
              withheldAmount: 0,
              holdbackPercent: 0,
              holdbackReason: reason,
              estimatedArrivalDate: undefined,
              receivedAt: undefined,
            }
          : draw);
        const next = { ...current, draws, invoices: [] };
        return { ...next, invoices: deriveDrawInvoices(next) };
      };

      if (cloudStatus === "offline-demo") {
        setData(applyDenial);
        return;
      }
      if (!portalClientIdRef.current) throw new Error("Only the homeowner portal can deny an invoice.");
      setSyncing(true);
      setCloudError("");
      try {
        await callPortal(portalClientIdRef.current, portalTokenRef.current, {
          action: "deny-invoice",
          requestId,
          denialReason: reason,
        });
        await refresh();
        recordDiagnostic({ level: "PASS", category: "network", event: "data.invoice-denied", message: "Homeowner denial and reason were saved and verified." });
      } catch (error) {
        const message = error instanceof Error ? error.message : "The shared workspace rejected this invoice denial.";
        setCloudError(message);
        recordDiagnostic({ level: "FAIL", category: "network", event: "data.invoice-denied", message });
        await refresh().catch(() => undefined);
        throw error;
      } finally {
        setSyncing(false);
      }
    };

    const decideDrawRequest = async (requestId: string, decisions: HomeownerDecisionDraft[]) => {
      const requestDraws = data.draws.filter((item) => (item.requestId || item.id) === requestId);
      if (!requestDraws.length) throw new Error("Payment request not found.");
      if (requestDraws.some((draw) => draw.status !== "CLIENT_REVIEW")) throw new Error("This request changed while you were reviewing it. Reload it before submitting.");
      const uniqueDecisionIds = new Set(decisions.map((decision) => decision.drawId));
      if (uniqueDecisionIds.size !== decisions.length || decisions.some((decision) => !requestDraws.some((draw) => draw.id === decision.drawId))) {
        throw new Error("Each requested SOW must have exactly one decision.");
      }
      const decisionDrafts = Object.fromEntries(decisions.map((decision) => [decision.drawId, decision])) as HomeownerDecisionDrafts;
      const normalized = buildHomeownerDecisionBatch(requestDraws.map((draw) => draw.id), decisionDrafts);
      const approvedAt = new Date();
      const arrival = new Date(approvedAt.getTime() + 48 * 60 * 60 * 1000).toISOString();
      const decisionByDraw = new Map(normalized.map((decision) => [decision.drawId, decision]));

      const applyDecisionBatch = (current: AppData) => {
        const draws = current.draws.map((item) => {
          const decision = decisionByDraw.get(item.id);
          if (!decision || item.status !== "CLIENT_REVIEW") return item;
          if (decision.decision === "REQUEST_CHANGES") {
            return {
              ...item,
              status: "CHANGES_REQUESTED" as const,
              approvedAmount: undefined,
              withheldAmount: 0,
              holdbackPercent: 0,
              holdbackReason: decision.reason,
              estimatedArrivalDate: undefined,
              receivedAt: undefined,
            };
          }
          const amounts = holdbackForAmount(item.amount, decision.holdbackPercent);
          return {
            ...item,
            status: "APPROVED" as const,
            approvedAmount: amounts.approvedAmount,
            withheldAmount: amounts.withheldAmount,
            holdbackPercent: decision.holdbackPercent,
            holdbackReason: decision.reason,
            estimatedArrivalDate: arrival,
          };
        });
        const next = { ...current, draws, invoices: [] };
        return { ...next, invoices: deriveDrawInvoices(next) };
      };

      if (cloudStatus === "offline-demo") {
        setData(applyDecisionBatch);
        return;
      }
      if (!portalClientIdRef.current) throw new Error("Only the homeowner portal can submit customer SOW decisions.");
      setSyncing(true);
      setCloudError("");
      try {
        const payload = await callPortal(portalClientIdRef.current, portalTokenRef.current, {
          action: "decide-request",
          requestId,
          decisions: normalized,
        });
        const receipt = payload.request && typeof payload.request === "object" ? payload.request as Record<string, unknown> : null;
        if (payload.decided !== true || receipt?.requestId !== requestId || receipt?.decidedCount !== normalized.length) {
          throw new Error("The shared workspace did not verify the complete SOW decision batch. Nothing is being reported as submitted.");
        }
        await refresh();
        recordDiagnostic({ level: "PASS", category: "network", event: "data.sow-decision-batch", message: `All ${normalized.length} SOW decisions were saved and verified together.` });
      } catch (error) {
        const message = error instanceof Error ? error.message : "The shared workspace rejected the complete SOW decision batch.";
        setCloudError(message);
        recordDiagnostic({ level: "FAIL", category: "network", event: "data.sow-decision-batch", message });
        await refresh().catch(() => undefined);
        throw error;
      } finally {
        setSyncing(false);
      }
    };

    return ({
    clearAllData() {
      if (cloudStatus !== "offline-demo") throw new Error("Bulk deletion is disabled for the shared workspace. Delete only the records you choose.");
      setData(EMPTY_DATA);
    },
    restoreDemoData() {
      if (cloudStatus !== "offline-demo") {
        setCloudError("Demo records cannot replace live company data. Delete or add only the records you choose.");
        return;
      }
      setData(seedData);
      recordDiagnostic({ level: "PASS", category: "application", event: "data.demo-restored", message: "Demo records restored after owner confirmation.", action: "restore", target: "demo-data" });
    },
    async updateCompanyProfile(input: Omit<CompanyProfile, "id">, logoFile?: File) {
      if (logoFile) validateLogoFile(logoFile);
      const updated: CompanyProfile = {
        id: "primary",
        legalName: input.legalName.trim(),
        displayName: input.displayName.trim(),
        address: input.address.trim(),
        phone: input.phone.trim(),
        email: input.email.trim().toLowerCase(),
        website: input.website.trim(),
        logoUrl: input.logoUrl.trim() || "/aqua-homes-watermark.png",
      };
      if (!updated.legalName || !updated.displayName || !updated.address || !updated.phone || !updated.email || !updated.website) {
        throw new Error("Company name, address, phone, email, and website are required for client invoices.");
      }
      const supabase = getCloudClient();
      if (!supabase || cloudStatus === "offline-demo") {
        const localUpdated = { ...updated, logoUrl: logoFile ? await fileToDataUrl(logoFile) : updated.logoUrl };
        setData((current) => ({ ...current, companyProfile: localUpdated }));
        return localUpdated;
      }
      setSyncing(true);
      setCloudError("");
      let uploadedPath = "";
      try {
        if (logoFile) {
          const extension = logoFile.type === "image/png" ? "png" : logoFile.type === "image/webp" ? "webp" : "jpg";
          uploadedPath = `primary/logo-${Date.now()}-${crypto.randomUUID()}.${extension}`;
          const upload = await supabase.storage.from("aquadraw-company-assets").upload(uploadedPath, logoFile, { contentType: logoFile.type, upsert: false });
          if (upload.error) throw upload.error;
          updated.logoUrl = supabase.storage.from("aquadraw-company-assets").getPublicUrl(uploadedPath).data.publicUrl;
        }
        const result = await supabase.from("company_profiles").upsert({
          id: updated.id,
          legal_name: updated.legalName,
          display_name: updated.displayName,
          address: updated.address,
          phone: updated.phone,
          email: updated.email,
          website: updated.website,
          logo_url: updated.logoUrl,
          updated_at: new Date().toISOString(),
        }).select("id").single();
        if (result.error) throw result.error;
        await refresh();
        recordDiagnostic({ level: "PASS", category: "network", event: "data.company-profile-updated", message: "Company invoice information saved and verified." });
        return updated;
      } catch (error) {
        if (uploadedPath) await supabase.storage.from("aquadraw-company-assets").remove([uploadedPath]).catch(() => undefined);
        const message = error instanceof Error ? error.message : "The shared workspace rejected the company invoice information.";
        setCloudError(message);
        recordDiagnostic({ level: "FAIL", category: "network", event: "data.company-profile-updated", message });
        throw error;
      } finally {
        setSyncing(false);
      }
    },
    updateClient(id: string, input: Omit<Client, "id">) {
      const updated: Client = {
        id,
        name: input.name.trim(),
        company: input.company?.trim() || undefined,
        email: input.email.trim().toLowerCase(),
        phone: input.phone.trim(),
        address: input.address.trim(),
        gcFeePercent: normalizeGcFeePercent(input.gcFeePercent),
      };
      if (!updated.name || !updated.email || !updated.phone || !updated.address) {
        throw new Error("Client name, email, phone, and contact address are required.");
      }
      setData((current) => ({ ...current, clients: current.clients.map((client) => client.id === id ? updated : client) }));
      const supabase = getCloudClient();
      if (supabase) runRemote("data.client-updated", async () => {
        const result = await supabase.from("clients").update({
          name: updated.name,
          company: updated.company || null,
          email: updated.email,
          phone: updated.phone,
          address: updated.address,
          gc_fee_percent: updated.gcFeePercent,
          updated_at: new Date().toISOString(),
        }).eq("id", id).select("id").single();
        if (result.error) throw result.error;
      });
      return updated;
    },
    deleteClient(id: string) {
      setData((current) => {
        const jobIds = new Set(current.jobs.filter((job) => job.clientId === id).map((job) => job.id));
        const sowIds = new Set(current.sowItems.filter((sow) => jobIds.has(sow.jobId)).map((sow) => sow.id));
        const next = { ...current, clients: current.clients.filter((client) => client.id !== id), jobs: current.jobs.filter((job) => !jobIds.has(job.id)), sowItems: current.sowItems.filter((sow) => !sowIds.has(sow.id)), draws: current.draws.filter((draw) => !sowIds.has(draw.sowId)), invoices: [] };
        return { ...next, invoices: deriveDrawInvoices(next) };
      });
      const supabase = getCloudClient();
      if (supabase) runRemote("data.client-deleted", async () => { const result = await supabase.from("clients").delete().eq("id", id); if (result.error) throw result.error; });
    },
    updateJob(id: string, input: Omit<AppData["jobs"][number], "id" | "clientId">) {
      const updated = {
        title: input.title.trim(),
        address: input.address.trim(),
        status: input.status,
        referenceNumber: input.referenceNumber?.trim() || "",
        startDate: input.startDate?.trim() || "",
        targetCompletion: input.targetCompletion?.trim() || "",
        projectManager: input.projectManager?.trim() || "",
        customerName: input.customerName?.trim() || "",
        contractAmount: Number(input.contractAmount || 0),
        contractType: input.contractType?.trim() || "",
      };
      if (!updated.title || !updated.address) throw new Error("Project name and address are required.");
      if (!Number.isFinite(updated.contractAmount) || updated.contractAmount < 0) throw new Error("Contract amount cannot be negative.");
      if (updated.startDate && updated.targetCompletion && updated.targetCompletion < updated.startDate) throw new Error("Target completion cannot be before the start date.");
      setData((current) => {
        const next = { ...current, jobs: current.jobs.map((job) => job.id === id ? { ...job, ...updated } : job), invoices: [] };
        return { ...next, invoices: deriveDrawInvoices(next) };
      });
      const supabase = getCloudClient();
      if (supabase) runRemote("data.job-updated", async () => {
        const result = await supabase.from("jobs").update({
          title: updated.title,
          address: updated.address,
          status: updated.status,
          reference_number: updated.referenceNumber || null,
          start_date: updated.startDate || null,
          target_completion_date: updated.targetCompletion || null,
          project_manager: updated.projectManager || null,
          customer_name: updated.customerName || null,
          contract_amount: updated.contractAmount || null,
          contract_type: updated.contractType || null,
          updated_at: new Date().toISOString(),
        }).eq("id", id).select("id").single();
        if (result.error) throw result.error;
      });
      return updated;
    },
    deleteJob(id: string) {
      setData((current) => {
        const sowIds = new Set(current.sowItems.filter((sow) => sow.jobId === id).map((sow) => sow.id));
        const next = { ...current, jobs: current.jobs.filter((job) => job.id !== id), sowItems: current.sowItems.filter((sow) => !sowIds.has(sow.id)), draws: current.draws.filter((draw) => !sowIds.has(draw.sowId)), invoices: [] };
        return { ...next, invoices: deriveDrawInvoices(next) };
      });
      const supabase = getCloudClient();
      if (supabase) runRemote("data.job-deleted", async () => { const result = await supabase.from("jobs").delete().eq("id", id); if (result.error) throw result.error; });
    },
    updateSow(id: string, input: Omit<AppData["sowItems"][number], "id" | "jobId">) {
      const updated = {
        title: input.title.trim(),
        description: input.description.trim(),
        contractAmount: Number(input.contractAmount),
        approvedBudgetChanges: Number(input.approvedBudgetChanges ?? 0),
        approvedChangeReason: input.approvedChangeReason?.trim() || "",
      };
      if (!updated.title || !updated.description) throw new Error("Category name and scope description are required.");
      if (!Number.isFinite(updated.contractAmount) || updated.contractAmount <= 0) throw new Error("Original SOW amount must be greater than zero.");
      if (!Number.isFinite(updated.approvedBudgetChanges) || updated.approvedBudgetChanges < 0) throw new Error("Approved Budget Changes / Overages cannot be negative.");
      if (updated.approvedBudgetChanges > 0 && !updated.approvedChangeReason) throw new Error("Explain why the approved budget changed.");
      setData((current) => {
        const next = { ...current, sowItems: current.sowItems.map((sow) => sow.id === id ? { ...sow, ...updated } : sow), invoices: [] };
        return { ...next, invoices: deriveDrawInvoices(next) };
      });
      const supabase = getCloudClient();
      if (supabase) runRemote("data.sow-updated", async () => {
        const result = await supabase.from("sow_items").update({
          title: updated.title,
          description: updated.description,
          contract_amount: updated.contractAmount,
          approved_budget_changes: updated.approvedBudgetChanges,
          approved_change_reason: updated.approvedChangeReason,
        }).eq("id", id).select("id").single();
        if (result.error) throw result.error;
      });
      return updated;
    },
    deleteSow(id: string) {
      setData((current) => {
        const next = { ...current, sowItems: current.sowItems.filter((sow) => sow.id !== id), draws: current.draws.filter((draw) => draw.sowId !== id), invoices: [] };
        return { ...next, invoices: deriveDrawInvoices(next) };
      });
      const supabase = getCloudClient();
      if (supabase) runRemote("data.sow-deleted", async () => { const result = await supabase.from("sow_items").delete().eq("id", id); if (result.error) throw result.error; });
    },
    updateDraw(id: string, input: { amount: number; explanation: string }) {
      const currentDraw = data.draws.find((draw) => draw.id === id);
      if (!currentDraw) throw new Error("Draw not found.");
      if (currentDraw.status !== "CLIENT_REVIEW") throw new Error("Approved invoice history cannot be edited.");
      const amount = Number(input.amount);
      const explanation = input.explanation.trim();
      if (!Number.isFinite(amount) || amount <= 0 || !explanation) throw new Error("Draw amount and notes are required.");
      const sow = data.sowItems.find((item) => item.id === currentDraw.sowId);
      const otherCommitted = data.draws.filter((draw) => draw.sowId === currentDraw.sowId && draw.id !== id).reduce((sum, draw) => sum + drawCommittedAmount(draw), 0);
      if (sow && amount > sow.contractAmount + (sow.approvedBudgetChanges ?? 0) - otherCommitted) throw new Error("The edited draw exceeds the available SOW budget.");
      setData((current) => {
        const draws = current.draws.map((draw) => draw.id === id ? { ...draw, amount, explanation } : draw);
        const next = { ...current, draws, invoices: [] };
        return { ...next, invoices: deriveDrawInvoices(next) };
      });
      const supabase = getCloudClient();
      if (supabase) runRemote("data.draw-updated", async () => { const result = await supabase.from("draws").update({ amount, explanation, updated_at: new Date().toISOString() }).eq("id", id).eq("status", "CLIENT_REVIEW").select("id").single(); if (result.error) throw result.error; });
      return { amount, explanation };
    },
    deleteDraw(id: string) {
      const currentDraw = data.draws.find((draw) => draw.id === id);
      if (currentDraw && currentDraw.status !== "CLIENT_REVIEW") throw new Error("Approved invoice history cannot be deleted.");
      setData((current) => {
        const next = { ...current, draws: current.draws.filter((draw) => draw.id !== id), invoices: [] };
        return { ...next, invoices: deriveDrawInvoices(next) };
      });
      const supabase = getCloudClient();
      if (supabase) runRemote("data.draw-deleted", async () => { const result = await supabase.from("draws").delete().eq("id", id); if (result.error) throw result.error; });
    },
    addClient(input: Omit<AppData["clients"][number], "id">) {
      const created = { ...input, gcFeePercent: normalizeGcFeePercent(input.gcFeePercent), id: crypto.randomUUID() };
      setData((current) => ({ ...current, clients: [...current.clients, created] }));
      const supabase = getCloudClient();
      if (supabase) runRemote("data.client-created", async () => {
        const result = await supabase.from("clients").insert({ id: created.id, name: created.name, company: created.company || null, email: created.email, phone: created.phone, address: created.address, gc_fee_percent: created.gcFeePercent });
        if (result.error) throw result.error;
      });
      return created;
    },
    addJob(input: Omit<AppData["jobs"][number], "id">) {
      const created = { ...input, id: crypto.randomUUID() };
      setData((current) => ({ ...current, jobs: [...current.jobs, created] }));
      const supabase = getCloudClient();
      if (supabase) runRemote("data.job-created", async () => {
        const result = await supabase.from("jobs").insert({
          id: created.id,
          client_id: created.clientId,
          address: created.address,
          title: created.title,
          status: created.status,
          reference_number: created.referenceNumber || null,
          start_date: created.startDate || null,
          target_completion_date: created.targetCompletion || null,
          project_manager: created.projectManager || null,
          customer_name: created.customerName || null,
          contract_amount: created.contractAmount || null,
          contract_type: created.contractType || null,
        });
        if (result.error) throw result.error;
      });
      return created;
    },
    addSow(input: Omit<AppData["sowItems"][number], "id">) {
      const created = { ...input, id: crypto.randomUUID() };
      setData((current) => ({ ...current, sowItems: [...current.sowItems, created] }));
      const supabase = getCloudClient();
      if (supabase) runRemote("data.sow-created", async () => {
        const result = await supabase.from("sow_items").insert({ id: created.id, job_id: created.jobId, title: created.title, description: created.description, contract_amount: created.contractAmount, approved_budget_changes: created.approvedBudgetChanges, approved_change_reason: created.approvedChangeReason || "" });
        if (result.error) throw result.error;
      });
      return created;
    },
    createDraw(input: Omit<Draw, "id" | "createdAt" | "status">) {
      const sow = data.sowItems.find((item) => item.id === input.sowId);
      if (!sow) throw new Error("SOW category not found.");
      const job = data.jobs.find((item) => item.id === sow.jobId);
      const client = data.clients.find((item) => item.id === job?.clientId);
      if (!job || !client) throw new Error("The customer for this SOW is unavailable.");
      const committed = data.draws.filter((draw) => draw.sowId === input.sowId).reduce((sum, draw) => sum + drawCommittedAmount(draw), 0);
      const available = sow.contractAmount + (sow.approvedBudgetChanges ?? 0) - committed;
      if (input.amount <= 0) throw new Error("Amount must be greater than zero.");
      if (input.amount > available) throw new Error(`Only $${available.toLocaleString()} is available.`);
      const requestId = input.requestId || crypto.randomUUID();
      const createdAt = new Date().toISOString();
      const created: Draw = { ...input, gcFeePercent: normalizeGcFeePercent(client.gcFeePercent), id: crypto.randomUUID(), requestId, requestNumber: input.requestNumber || createRequestNumber(requestId, createdAt), createdAt, status: "CLIENT_REVIEW" };
      setData((current) => {
        const next = { ...current, draws: [created, ...current.draws], invoices: [] };
        return { ...next, invoices: deriveDrawInvoices(next) };
      });
      const supabase = getCloudClient();
      if (supabase) runRemote("data.draw-created", async () => {
        const insert = await supabase.from("draws").insert({ id: created.id, sow_id: created.sowId, amount: created.amount, explanation: created.explanation, status: created.status, request_id: created.requestId, request_number: created.requestNumber, gc_fee_percent: created.gcFeePercent, created_at: created.createdAt });
        if (insert.error) throw insert.error;
        const names = created.mediaNames || [];
        for (let index = 0; index < (created.photoDataUrls || []).length; index += 1) {
          const blob = dataUrlToBlob(created.photoDataUrls![index]);
          const objectPath = `${created.sowId}/${created.id}/${crypto.randomUUID()}.jpg`;
          const upload = await supabase.storage.from("draw-photos").upload(objectPath, blob, { contentType: "image/jpeg", upsert: false });
          if (upload.error) throw upload.error;
          const photo = await supabase.from("draw_photos").insert({ draw_id: created.id, object_path: objectPath, display_name: names[index] || `Draw photo ${index + 1}` });
          if (photo.error) throw photo.error;
        }
      });
      return created;
    },
    async submitDrawRequest(input: { jobId: string; lines: Array<{ sowId: string; amount: number; explanation: string; photoFiles?: File[] }> }) {
      const requestId = crypto.randomUUID();
      const createdAt = new Date().toISOString();
      const requestNumber = createRequestNumber(requestId, createdAt);
      const lines = input.lines.filter((line) => Number(line.amount) > 0);
      if (!lines.length) throw new Error("Add at least one SOW line with an amount.");
      if (new Set(lines.map((line) => line.sowId)).size !== lines.length) throw new Error("Each SOW category can appear only once in a draw request.");
      const job = data.jobs.find((item) => item.id === input.jobId);
      if (!job) throw new Error("The selected project no longer exists.");
      const client = data.clients.find((item) => item.id === job.clientId);
      if (!client) throw new Error("The selected customer no longer exists.");
      const gcFeePercent = normalizeGcFeePercent(client.gcFeePercent);
      const created: Draw[] = lines.map((line) => {
        const sow = data.sowItems.find((item) => item.id === line.sowId && item.jobId === input.jobId);
        if (!sow) throw new Error("One selected SOW category no longer belongs to this job.");
        const committed = data.draws.filter((draw) => draw.sowId === line.sowId).reduce((sum, draw) => sum + drawCommittedAmount(draw), 0);
        const available = sow.contractAmount + (sow.approvedBudgetChanges ?? 0) - committed;
        if (!Number.isFinite(line.amount) || line.amount <= 0 || !line.explanation.trim()) throw new Error("Every request line needs an amount and notes.");
        if (line.amount > available) throw new Error(`${sow.title} has only ${available.toLocaleString()} available.`);
        for (const file of line.photoFiles || []) validatePhotoFile(file);
        return { id: crypto.randomUUID(), sowId: line.sowId, amount: Number(line.amount), explanation: line.explanation.trim(), status: "CLIENT_REVIEW", requestId, requestNumber, gcFeePercent, createdAt };
      });
      const thisDrawSubtotal = created.reduce((sum, draw) => sum + draw.amount, 0);
      const gcFeeAmount = gcFeeForSubtotal(thisDrawSubtotal, gcFeePercent);
      const thisDrawRequest = thisDrawSubtotal + gcFeeAmount;
      const jobSows = data.sowItems.filter((sow) => sow.jobId === input.jobId);
      const originalContract = Number(job.contractAmount) > 0 ? Number(job.contractAmount) : jobSows.reduce((sum, sow) => sum + sow.contractAmount, 0);
      const contractBalance = originalContract + jobSows.reduce((sum, sow) => sum + (sow.approvedBudgetChanges ?? 0), 0);
      const jobDraws = data.draws.filter((draw) => jobSows.some((sow) => sow.id === draw.sowId));
      const alreadyRequested = jobDraws.reduce((sum, draw) => sum + drawCommittedAmount(draw), 0);
      const priorInvoiceTotal = effectiveDrawInvoices(data).filter((invoice) => invoice.jobId === job.id).reduce((sum, invoice) => sum + invoice.requestedTotal, 0);
      const jobToDateReceived = jobDraws.filter((draw) => draw.status === "RECEIVED").reduce((sum, draw) => sum + drawApprovedAmount(draw), 0);
      const receiptData: AppData = { ...data, draws: [...data.draws, ...created], invoices: [] };
      const holdbackLedger = buildHoldbackLedger(receiptData);
      const holdbackCarryLines = created.map((draw) => {
        const flow = holdbackLedger.get(draw.id);
        return {
          drawId: draw.id,
          sowId: draw.sowId,
          priorHoldbackAvailable: flow?.priorHoldbackAvailable || 0,
          priorHoldbackApplied: flow?.priorHoldbackApplied || 0,
          priorHoldbackSources: flow?.priorHoldbackSources || [],
          holdbackBalanceAfter: flow?.holdbackBalanceAfter || 0,
        };
      });
      const holdbackCarryIn = holdbackCarryLines.reduce((sum, line) => sum + line.priorHoldbackApplied, 0);
      setData((current) => {
        const next = { ...current, draws: [...created, ...current.draws], invoices: [] };
        return { ...next, invoices: deriveDrawInvoices(next) };
      });
      const photoFailures: string[] = [];
      const supabase = getCloudClient();
      if (supabase) {
        setSyncing(true);
        setCloudError("");
        try {
          const insert = await supabase.from("draws").insert(created.map((draw) => ({ id: draw.id, sow_id: draw.sowId, amount: draw.amount, explanation: draw.explanation, status: draw.status, request_id: draw.requestId, request_number: draw.requestNumber, gc_fee_percent: draw.gcFeePercent, created_at: draw.createdAt })));
          if (insert.error) throw insert.error;
          for (const [lineIndex, line] of lines.entries()) {
            for (const file of line.photoFiles || []) {
              const objectPath = `${line.sowId}/${created[lineIndex].id}/${crypto.randomUUID()}-${file.name.replace(/[^a-zA-Z0-9._-]/g, "_")}`;
              const upload = await supabase.storage.from("draw-photos").upload(objectPath, file, { contentType: file.type, upsert: false });
              if (upload.error) { photoFailures.push(file.name); continue; }
              const photo = await supabase.from("draw_photos").insert({ draw_id: created[lineIndex].id, object_path: objectPath, display_name: file.name });
              if (photo.error) photoFailures.push(file.name);
            }
          }
          await refresh();
          recordDiagnostic({ level: photoFailures.length ? "LIMITATION" : "PASS", category: "network", event: "data.draw-request-created", message: photoFailures.length ? "Draw request saved; one or more photos need attention." : "Draw request and photographs saved and verified." });
        } catch (error) {
          const message = error instanceof Error ? error.message : "The shared workspace rejected this draw request.";
          setCloudError(message);
          recordDiagnostic({ level: "FAIL", category: "network", event: "data.draw-request-created", message });
          await refresh().catch(() => undefined);
          throw error;
        } finally {
          setSyncing(false);
        }
      }
      return { requestId, requestNumber, createdAt, lines: created, thisDrawSubtotal, gcFeePercent, gcFeeAmount, thisDrawRequest, jobToDateRequested: priorInvoiceTotal + thisDrawRequest, jobToDateReceived, remainingContractBalance: contractBalance - alreadyRequested - thisDrawSubtotal, holdbackCarryIn, holdbackCarryLines, photoFailures };
    },
    async updatePendingInvoice(input: {
      requestId: string;
      lines: Array<{ drawId: string; amount: number; explanation: string; photoFiles?: File[] }>;
    }) {
      const requestDraws = data.draws.filter((draw) => (draw.requestId || draw.id) === input.requestId);
      if (!requestDraws.length) throw new Error("Invoice not found.");
      const pendingDraws = requestDraws.filter((draw) => draw.status === "CLIENT_REVIEW");
      if (pendingDraws.length > 0 && pendingDraws.length < requestDraws.length) throw new Error("Finish the homeowner review before revising requested changes.");
      const editableDraws = pendingDraws.length === requestDraws.length
        ? requestDraws
        : requestDraws.filter((draw) => draw.status === "CHANGES_REQUESTED");
      if (!editableDraws.length) throw new Error("Approved invoice history cannot be edited.");
      if (input.lines.length !== editableDraws.length || new Set(input.lines.map((line) => line.drawId)).size !== editableDraws.length) {
        throw new Error(pendingDraws.length ? "Save every pending invoice line together." : "Save every declined SOW line together.");
      }

      const prepared = input.lines.map((line) => {
        const draw = editableDraws.find((item) => item.id === line.drawId);
        if (!draw) throw new Error("One invoice line no longer belongs to this request.");
        const amount = Number(line.amount);
        const explanation = line.explanation.trim();
        if (!Number.isFinite(amount) || amount <= 0 || !explanation) throw new Error("Every invoice line needs an amount and notes.");
        if (explanation.length > 4000) throw new Error("Invoice line notes must be 4,000 characters or fewer.");
        const sow = data.sowItems.find((item) => item.id === draw.sowId);
        const otherCommitted = data.draws
          .filter((item) => item.sowId === draw.sowId && item.id !== draw.id)
          .reduce((sum, item) => sum + drawCommittedAmount(item), 0);
        if (sow && amount > sow.contractAmount + (sow.approvedBudgetChanges || 0) - otherCommitted) {
          throw new Error(`${sow.title} does not have enough remaining budget for that edit.`);
        }
        for (const file of line.photoFiles || []) validatePhotoFile(file);
        return { draw, amount, explanation, photoFiles: line.photoFiles || [] };
      });

      if (cloudStatus === "offline-demo") {
        const localPhotos = new Map<string, { names: string[]; urls: string[] }>();
        for (const line of prepared) {
          localPhotos.set(line.draw.id, {
            names: line.photoFiles.map((file) => file.name),
            urls: await Promise.all(line.photoFiles.map(fileToDataUrl)),
          });
        }
        setData((current) => {
          const draws = current.draws.map((draw) => {
            const line = prepared.find((item) => item.draw.id === draw.id);
            if (!line) return draw;
            const photos = localPhotos.get(draw.id) || { names: [], urls: [] };
            return {
              ...draw,
              amount: line.amount,
              explanation: line.explanation,
              status: "CLIENT_REVIEW" as const,
              approvedAmount: undefined,
              withheldAmount: 0,
              holdbackPercent: 0,
              holdbackReason: "",
              estimatedArrivalDate: undefined,
              receivedAt: undefined,
              mediaNames: [...(draw.mediaNames || []), ...photos.names],
              photoDataUrls: [...(draw.photoDataUrls || []), ...photos.urls],
            };
          });
          const next = { ...current, draws, invoices: [] };
          return { ...next, invoices: deriveDrawInvoices(next) };
        });
        return { photoFailures: [] as string[] };
      }

      if (portalClientIdRef.current) throw new Error("Only the owner can edit a pending invoice.");
      const supabase = getCloudClient();
      if (!supabase) throw new Error("The shared workspace is not configured.");
      setSyncing(true);
      setCloudError("");
      const photoFailures: string[] = [];
      try {
        const edit = await supabase.rpc("edit_pending_draw_request", {
          p_request_id: input.requestId,
          p_lines: prepared.map((line) => ({
            draw_id: line.draw.id,
            amount: line.amount,
            explanation: line.explanation,
          })),
        });
        if (edit.error) throw edit.error;

        for (const line of prepared) {
          for (const file of line.photoFiles) {
            const objectPath = `${line.draw.sowId}/${line.draw.id}/${crypto.randomUUID()}-${file.name.replace(/[^a-zA-Z0-9._-]/g, "_")}`;
            const upload = await supabase.storage.from("draw-photos").upload(objectPath, file, { contentType: file.type, upsert: false });
            if (upload.error) { photoFailures.push(file.name); continue; }
            const photo = await supabase.from("draw_photos").insert({ draw_id: line.draw.id, object_path: objectPath, display_name: file.name });
            if (photo.error) photoFailures.push(file.name);
          }
        }

        await refresh();
        recordDiagnostic({
          level: photoFailures.length ? "LIMITATION" : "PASS",
          category: "network",
          event: "data.pending-invoice-edited",
          message: photoFailures.length
            ? "Pending invoice values were saved; one or more added photos need attention."
            : "Pending invoice values and added photographs were saved and verified.",
        });
        return { photoFailures };
      } catch (error) {
        const message = error instanceof Error ? error.message : "The shared workspace rejected this pending invoice edit.";
        setCloudError(message);
        recordDiagnostic({ level: "FAIL", category: "network", event: "data.pending-invoice-edited", message });
        await refresh().catch(() => undefined);
        throw error;
      } finally {
        setSyncing(false);
      }
    },
    approveInvoice,
    denyInvoice,
    decideDrawRequest,
    approveDraw(id: string) {
      const draw = data.draws.find((item) => item.id === id);
      if (!draw) return;
      void approveInvoice(draw.requestId || draw.id, 0, "");
    },
    receivePayment(id: string) {
      setData((current) => {
        const draws = current.draws.map((draw) => draw.id === id && draw.status === "APPROVED" ? { ...draw, status: "RECEIVED" as const, receivedAt: new Date().toISOString() } : draw);
        const next = { ...current, draws, invoices: [] };
        return { ...next, invoices: deriveDrawInvoices(next) };
      });
      const supabase = getCloudClient();
      if (supabase) runRemote("data.draw-payment-received", async () => {
        const result = await supabase.from("draws").update({ status: "RECEIVED", received_at: new Date().toISOString() }).eq("id", id).eq("status", "APPROVED");
        if (result.error) throw result.error;
      });
    },
    receiveInvoicePayment(requestId: string) {
      const receivedAt = new Date().toISOString();
      const drawIds = invoicePaymentDrawIds(data, requestId);
      if (!drawIds.length) return;
      const selected = new Set(drawIds);
      setData((current) => {
        const draws = current.draws.map((draw) => selected.has(draw.id) && draw.status === "APPROVED"
          ? { ...draw, status: "RECEIVED" as const, receivedAt }
          : draw);
        const next = { ...current, draws, invoices: [] };
        return { ...next, invoices: deriveDrawInvoices(next) };
      });
      const supabase = getCloudClient();
      if (supabase) runRemote("data.invoice-payment-received", async () => {
        const result = await supabase.from("draws").update({ status: "RECEIVED", received_at: receivedAt }).in("id", drawIds).eq("status", "APPROVED");
        if (result.error) throw result.error;
      });
    },
  });
  }, [cloudStatus, data, refresh, runRemote]);

  const signIn = useCallback(async (email: string, password: string) => {
    const supabase = getCloudClient();
    if (!supabase) throw new Error("The shared workspace is not configured.");
    setCloudError("");
    const result = await supabase.auth.signInWithPassword({ email, password });
    if (result.error) throw result.error;
    await refresh();
  }, [refresh]);

  const signUp = useCallback(async (email: string, password: string) => {
    const supabase = getCloudClient();
    if (!supabase) throw new Error("The shared workspace is not configured.");
    const nativeMode = document.documentElement.dataset.aquadrawNative === "true";
    const result = await supabase.auth.signUp({ email, password, options: { emailRedirectTo: nativeMode ? AQUA_PUBLIC_APP_URL : window.location.origin } });
    if (result.error) throw result.error;
    return Boolean(result.data.session);
  }, []);

  const signOut = useCallback(async () => {
    const supabase = getCloudClient();
    if (supabase) await supabase.auth.signOut();
    setData(EMPTY_DATA);
    setCloudStatus("signed-out");
  }, []);

  const issuePortalLink = useCallback(async (clientId: string) => {
    const clientRecord = data.clients.find((item) => item.id === clientId);
    if (!clientRecord) throw new Error("Client record not found.");
    const supabase = getCloudClient();
    if (!supabase) throw new Error("The shared workspace is not configured.");
    const portalToken = createPortalToken();
    const portalTokenHash = await sha256Hex(portalToken);
    const result = await supabase.from("clients")
      .update({ portal_token_hash: portalTokenHash, updated_at: new Date().toISOString() })
      .eq("id", clientId)
      .select("portal_token_hash")
      .single();
    if (result.error || result.data?.portal_token_hash !== portalTokenHash) {
      throw result.error || new Error("The secure portal link could not be verified.");
    }
    return buildClientPortalUrl(clientId, portalToken);
  }, [data.clients]);

  return { data, hydrated, cloudStatus, cloudError, ownerEmail, syncing, portalMode: cloudStatus === "portal" || cloudStatus === "portal-signed-out", refresh, signIn, signUp, signOut, issuePortalLink, ...actions };
}
